/** Project detail — port of the original views.project: stat cards and five tabs chosen by `?t=`. */
import { Fragment, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import type { Backup, Project, ProjectUpdate, WorkLog } from '@/types/models';
import { byDateDesc, fmtDate, fmtH, fmtNum, sum } from '@/domain/format';
import { KINDS } from '@/domain/metrics';
import { deadline } from '@/domain/projects';
import { useAction, useDataset } from '@/hooks/data';
import { PermissionGate } from '@/auth/ProtectedRoute';
import { useAuth, useModal, useToast } from '@/app/AppContext';
import { ActionsCell, DeadlineCell, ErrorState, IconButton, KindTag, LoadingState, MemberLink, NoRecords, NotFound, StatCard, StatusBadge, Topbar, useConfirm } from '@/ui/kit';
import { useAppActions } from '../actions';
import { WorkLogTable } from '../worklog/WorkLogTable';
import { LinkModal } from './LinkModal';
import { MetricKeysModal } from './MetricKeysModal';
import { metricTable } from './metricTable';
import { useProjectPatch } from './useProjectPatch';

const TABS = [
  ['overview', 'Overview'],
  ['members', 'Members'],
  ['work', 'Work Log'],
  ['analytics', 'Project Log / Analytics'],
  ['links', 'Links'],
] as const;

export function ProjectDetailPage() {
  const { id = '' } = useParams();
  const [q] = useSearchParams();
  const ds = useDataset();
  const header = (
    <Topbar title="Project">
      <Link className="btn" to="/projects">
        ← All projects
      </Link>
    </Topbar>
  );
  if (ds.error) return (<>{header}<ErrorState error={ds.error} onRetry={ds.refetch} /></>);
  if (!ds.data) return (<>{header}<LoadingState /></>);
  const p = ds.data.projects.find((x) => x.id === id);
  if (!p) return <NotFound what="Project" />;
  return <ProjectDetail data={ds.data} project={p} tab={q.get('t') || 'overview'} />;
}

interface TabProps {
  data: Backup;
  project: Project;
  /** The project's work logs, newest first */
  logs: WorkLog[];
  /** The project's Project Logs, newest first */
  ups: ProjectUpdate[];
}

function ProjectDetail({ data, project: p, tab }: { data: Backup; project: Project; tab: string }) {
  const nav = useNavigate();
  const act = useAppActions();
  const logs = data.logs.filter((l) => l.projectId === p.id).sort(byDateDesc);
  const ups = data.updates.filter((u) => u.projectId === p.id).sort(byDateDesc);
  const base = `/projects/${encodeURIComponent(p.id)}`;
  const props: TabProps = { data, project: p, logs, ups };
  return (
    <>
      <Topbar
        title={
          <>
            {p.name} <StatusBadge status={p.status} style={{ verticalAlign: 'middle', marginLeft: 6 }} />
          </>
        }
        sub={p.service}
      >
        <Link className="btn" to="/projects">
          ← All projects
        </Link>
        <PermissionGate perm="projects:write">
          <button className="btn" onClick={() => act.project(p.id)}>
            Edit Project
          </button>
        </PermissionGate>
        <PermissionGate perm="updates:write">
          <button className="btn" onClick={() => act.projectLog({ projectId: p.id })}>
            + Project Log Work
          </button>
        </PermissionGate>
        <PermissionGate perm="worklog:write">
          <button className="btn primary" onClick={() => act.workLog({ projectId: p.id })}>
            + Employee Log Work
          </button>
        </PermissionGate>
      </Topbar>
      <div className="cards">
        <StatCard to={`${base}?t=members`} n={p.members.length} label="Members" />
        <StatCard to={`${base}?t=work`} n={fmtH(sum(logs.map((l) => l.hours)))} label="Hours logged" />
        <StatCard to={`${base}?t=analytics`} n={ups.length} label="Project logs" />
        <StatCard to={`${base}?t=links`} n={p.links.length} label="Links" />
      </div>
      <div className="panel">
        <div className="panel-b">
          <div className="tabs">
            {TABS.map(([k, l]) => (
              <button key={k} type="button" className={'tab' + (k === tab ? ' active' : '')} aria-current={k === tab ? 'page' : undefined} onClick={() => nav(`${base}?t=${k}`)}>
                {l}
              </button>
            ))}
          </div>
          {tab === 'overview' ? <OverviewTab {...props} /> : null}
          {tab === 'members' ? <MembersTab {...props} /> : null}
          {tab === 'work' ? <WorkTab {...props} /> : null}
          {tab === 'analytics' ? <AnalyticsTab {...props} /> : null}
          {tab === 'links' ? <LinksTab {...props} /> : null}
        </div>
      </div>
    </>
  );
}

/** Hours per member on this project. */
const hoursByMember = (logs: WorkLog[]) => {
  const mh: Record<string, number> = {};
  logs.forEach((l) => (mh[l.memberId] = (mh[l.memberId] || 0) + Number(l.hours)));
  return mh;
};

/* ---------- Overview ---------- */

function OverviewTab({ data, project: p, logs, ups }: TabProps) {
  const act = useAppActions();
  const auth = useAuth();
  const mname = (id: string) => data.members.find((m) => m.id === id)?.name || '—';
  const mh = hoursByMember(logs);
  const latest = ups[0];
  return (
    <>
      <div className="grid2">
        <div>
          <label>Service</label>
          <div>{p.service || '—'}</div>
        </div>
        <div>
          <label>Lead / Owner</label>
          <div>{p.lead || '—'}</div>
        </div>
        <div>
          <label>Deadline</label>
          <div>
            <DeadlineCell project={p} onSet={auth.can('projects:write') ? () => act.deadline(p.id) : undefined} />
          </div>
          {p.revised ? <div className="hint">Original: {fmtDate(p.planned)}</div> : null}
          {p.deadlineBasis ? <div className="hint">Basis: {p.deadlineBasis}</div> : null}
        </div>
        <div>
          <label>{' '}</label>
          <PermissionGate perm="projects:write">
            <button className="btn sm" onClick={() => act.deadline(p.id)}>
              {deadline(p) ? 'Change Deadline' : 'Set Deadline'}
            </button>
          </PermissionGate>
        </div>
        <div>
          <label>Completed</label>
          <div>{p.completed || '—'}</div>
        </div>
        <div>
          <label>Pending</label>
          <div>{p.pending || '—'}</div>
        </div>
        <div>
          <label>Members</label>
          <div>
            {p.members.length} — {p.members.map(mname).join(', ') || <span className="muted">none yet</span>}
          </div>
        </div>
        <div>
          <label>Total hours logged</label>
          <div>
            {fmtH(sum(logs.map((l) => l.hours)))} across {Object.keys(mh).length} members
          </div>
        </div>
      </div>
      {(p.extensions || []).length ? (
        <div style={{ marginTop: 16 }}>
          <label>Deadline history</label>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>On</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {p.extensions
                  .slice()
                  .reverse()
                  .map((e, i) => (
                    <tr key={i}>
                      <td className="mono">{fmtDate(e.on)}</td>
                      <td className="mono">{fmtDate(e.from)}</td>
                      <td className="mono">{fmtDate(e.to)}</td>
                      <td>{e.reason}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      <div style={{ marginTop: 16 }}>
        <label>Latest project log</label>
        {latest ? (
          <>
            <div className="hint">{fmtDate(latest.date)}</div>
            <div>{latest.note || <span className="muted">No note.</span>}</div>
            {latest.metrics.length ? (
              <div className="row" style={{ marginTop: 8 }}>
                {latest.metrics.map((m, i) => (
                  <span className="chip" key={i}>
                    {m.label}: <b>{fmtNum(m.value)}</b>
                  </span>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <div className="muted">No updates yet.</div>
        )}
      </div>
    </>
  );
}

/* ---------- Members ---------- */

function MembersTab({ data, project: p, logs }: TabProps) {
  const act = useAppActions();
  const auth = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const save = useProjectPatch(p.id);
  const [sel, setSel] = useState('');
  const mh = hoursByMember(logs);
  const mname = (id: string) => data.members.find((m) => m.id === id)?.name || '—';
  const avail = data.members.filter((m) => !p.members.includes(m.id));
  const addMember = async () => {
    if (!sel) {
      toast('Please select a member.', true);
      return;
    }
    if (await save.run({ members: [...p.members, sel] }, 'Member added successfully.')) setSel('');
  };
  const removeMember = (mid: string) =>
    confirm(`Are you sure you want to remove ${mname(mid)} from ${p.name}? Their work logs will be kept.`, () =>
      save.run({ members: p.members.filter((x) => x !== mid) }, 'Member removed successfully.'),
    );
  const rows = p.members.map((mid, i) => {
    const m = data.members.find((x) => x.id === mid);
    if (!m) return null;
    const last = logs.find((l) => l.memberId === mid);
    return (
      <tr key={mid}>
        <td>{i + 1}</td>
        <td>
          <MemberLink id={mid} name={m.name} />
        </td>
        <td className="muted">{m.role || ''}</td>
        <td className="num">{fmtH(mh[mid] || 0)}</td>
        <td>{last ? last.work : '—'}</td>
        <td className="mono">{last ? fmtDate(last.date) : '—'}</td>
        <td>
          <PermissionGate perm="projects:write" fallback={<span className="muted">—</span>}>
            <IconButton icon="del" tip="Remove" danger onClick={() => removeMember(mid)} />
          </PermissionGate>
        </td>
      </tr>
    );
  });
  return (
    <>
      {auth.can('projects:write') || auth.can('members:write') ? (
        <div className="row" style={{ marginBottom: 12 }}>
          <PermissionGate perm="projects:write">
            <select style={{ maxWidth: 260 }} value={sel} onChange={(e) => setSel(e.target.value)} aria-label="Select a member to add">
              <option value="">Select a member to add</option>
              {avail.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <button className="btn primary" onClick={() => void addMember()} disabled={save.pending}>
              Add Member
            </button>
          </PermissionGate>
          <PermissionGate perm="members:write">
            <button className="btn" onClick={() => act.member({ projectId: p.id })}>
              + New Member
            </button>
          </PermissionGate>
        </div>
      ) : null}
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>SR. No.</th>
              <th>Member</th>
              <th>Role</th>
              <th className="num">Hours on project</th>
              <th>Last work done</th>
              <th>Last date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>{rows.some(Boolean) ? rows : <NoRecords cols={7} />}</tbody>
        </table>
      </div>
    </>
  );
}

/* ---------- Work Log ---------- */

function WorkTab({ data, project: p, logs }: TabProps) {
  const act = useAppActions();
  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <PermissionGate perm="worklog:write">
          <button className="btn primary" onClick={() => act.workLog({ projectId: p.id })}>
            + Employee Log Work
          </button>
        </PermissionGate>
        <span className="hint">
          {logs.length} entries · {fmtH(sum(logs.map((l) => l.hours)))}
        </span>
      </div>
      <WorkLogTable logs={logs} projects={data.projects} members={data.members} hideProject />
    </>
  );
}

/* ---------- Project Log / Analytics ---------- */

function AnalyticsTab({ project: p, ups }: TabProps) {
  const act = useAppActions();
  const modal = useModal();
  const confirm = useConfirm();
  const del = useAction(async (s, id: string) => {
    await s.projectUpdates.remove(id);
    return true;
  }, 'Project log deleted successfully.');
  const { dates, rows } = metricTable(p, ups);
  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <PermissionGate perm="updates:write">
          <button className="btn primary" onClick={() => act.projectLog({ projectId: p.id })}>
            + Project Log Work
          </button>
        </PermissionGate>
        <PermissionGate perm="projects:write">
          <button className="btn" onClick={() => modal.open((close) => <MetricKeysModal projectId={p.id} onClose={close} />, { wide: true })}>
            Configure metrics
          </button>
        </PermissionGate>
        <span className="hint">Metrics are free-form — add any label you like per day.</span>
      </div>
      {rows.length ? (
        <>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Type</th>
                  {dates.map((d, i) => (
                    <th className="num" key={i}>
                      {fmtDate(d)}
                    </th>
                  ))}
                  <th className="num">{dates.length > 1 ? 'Change / Total' : ''}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, ri) => {
                  const s = r.summary;
                  return (
                    <tr key={ri}>
                      <td>{r.label}</td>
                      <td>
                        <KindTag kind={r.kind} />
                      </td>
                      {r.values.map((v, i) => (
                        <td className="num" key={i}>
                          {v !== null ? fmtNum(v) : <span className="muted">—</span>}
                        </td>
                      ))}
                      <td className="num">
                        {s?.type === 'total' ? (
                          <>
                            <b>{fmtNum(s.total)}</b>{' '}
                            <span className="hint">
                              in {s.days} day{s.days === 1 ? '' : 's'}
                            </span>
                          </>
                        ) : s?.type === 'change' ? (
                          <>
                            <b style={{ color: s.change > 0 ? 'var(--success)' : s.change < 0 ? 'var(--danger)' : 'inherit' }}>
                              {s.change > 0 ? '+' : ''}
                              {fmtNum(s.change)}
                              {s.pct ? ' pts' : ''}
                            </b>{' '}
                            <span className="hint">
                              {fmtDate(s.from).slice(0, 5)} → {fmtDate(s.to).slice(0, 5)}
                            </span>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            {(Object.keys(KINDS) as (keyof typeof KINDS)[]).map((k) => (
              <Fragment key={k}>
                <KindTag kind={k} /> {KINDS[k][1]}
                <br />
              </Fragment>
            ))}
            <KindTag kind="" /> Nobody has set the type, so no total or change is shown. Set it in Configure metrics.
          </div>
        </>
      ) : (
        <div className="empty">No metrics yet. Add a Project Log Work with statistics, or configure metric names.</div>
      )}
      <h3 style={{ fontSize: 14, margin: '18px 0 8px' }}>Daily notes</h3>
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 110 }}>Date</th>
              <th>Note</th>
              <th>Metrics</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ups.length ? (
              ups.map((u) => (
                <tr key={u.id}>
                  <td className="mono">{fmtDate(u.date)}</td>
                  <td>{u.note || <span className="muted">—</span>}</td>
                  <td>{u.metrics.length ? `${u.metrics.length} values` : <span className="muted">—</span>}</td>
                  <td>
                    <PermissionGate perm="updates:write" fallback={<span className="muted">—</span>}>
                      <ActionsCell>
                        <IconButton icon="edit" tip="Edit" onClick={() => act.projectLog({ id: u.id })} />
                        <IconButton icon="del" tip="Delete" danger onClick={() => confirm(`Are you sure you want to delete the ${fmtDate(u.date)} update for ${p.name}?`, () => del.run(u.id))} />
                      </ActionsCell>
                    </PermissionGate>
                  </td>
                </tr>
              ))
            ) : (
              <NoRecords cols={4} />
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------- Links ---------- */

function LinksTab({ project: p }: TabProps) {
  const modal = useModal();
  const confirm = useConfirm();
  const save = useProjectPatch(p.id);
  const openLink = (index?: number) => modal.open((close) => <LinkModal projectId={p.id} index={index} onClose={close} />);
  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <PermissionGate perm="projects:write">
          <button className="btn primary" onClick={() => openLink()}>
            + Add Link
          </button>
        </PermissionGate>
        <span className="hint">Website, admin panel, reports, results, sheets — anything with a URL.</span>
      </div>
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>SR. No.</th>
              <th>Label</th>
              <th>URL</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {p.links.length ? (
              p.links.map((l, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{l.label}</td>
                  <td>
                    <a href={l.url} target="_blank" rel="noopener noreferrer">
                      {l.url} ↗
                    </a>
                  </td>
                  <td>
                    <PermissionGate perm="projects:write" fallback={<span className="muted">—</span>}>
                      <ActionsCell>
                        <IconButton icon="edit" tip="Edit" onClick={() => openLink(i)} />
                        <IconButton
                          icon="del"
                          tip="Delete"
                          danger
                          onClick={() => confirm(`Are you sure you want to delete the link "${l.label}"?`, () => save.run({ links: p.links.filter((_, j) => j !== i) }, 'Link deleted successfully.'))}
                        />
                      </ActionsCell>
                    </PermissionGate>
                  </td>
                </tr>
              ))
            ) : (
              <NoRecords cols={4} />
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
